import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Briefcase, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const benefits = [
  {
    icon: Briefcase,
    title: "Meaningful Work",
    description: "Work on challenging projects that make a real impact for Fortune 500 clients."
  },
  {
    icon: Users,
    title: "Great Team",
    description: "Collaborate with talented professionals who are passionate about technology."
  },
  {
    icon: Clock,
    title: "Work-Life Balance",
    description: "Flexible work arrangements and generous PTO to maintain your well-being."
  }
];

const perks = [
  "Competitive salary and annual bonus",
  "Comprehensive health, dental, and vision insurance",
  "401(k) with company match",
  "Professional development budget",
  "Flexible work arrangements",
  "Generous PTO policy",
  "Certification reimbursement",
  "Team events and outings"
];

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState("All");

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${API}/jobs`);
        setJobs(response.data);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const departments = ["All", ...new Set(jobs.map(job => job.department))];
  
  const filteredJobs = selectedDepartment === "All" 
    ? jobs 
    : jobs.filter(job => job.department === selectedDepartment);

  return (
    <div data-testid="careers-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
              Join Our Team
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white font-['Outfit'] mb-6">
              Build Your Career With Us
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed font-['Plus_Jakarta_Sans']">
              Join a team of passionate technologists who are transforming businesses through 
              innovation. We offer exciting opportunities, competitive benefits, and a 
              collaborative culture.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="bg-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Why Nidus?
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Why You'll Love Working Here
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="p-8 border border-slate-200 hover:border-blue-600 transition-colors">
                <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mb-6">
                  <benefit.icon className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed font-['Plus_Jakarta_Sans']">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perks Section */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                Benefits & Perks
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit'] mb-6">
                We Take Care of Our People
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {perks.map((perk) => (
                  <div key={perk} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2" />
                    <span className="text-slate-600 text-sm font-['Plus_Jakarta_Sans']">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden border border-slate-200">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800"
                alt="Team collaboration"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Department Filter */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center gap-2">
            {departments.map((dept) => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                data-testid={`dept-${dept.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  selectedDepartment === dept
                    ? "bg-blue-700 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section data-testid="job-listings" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Open Positions
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Current Opportunities
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-slate-600">Loading positions...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job, index) => (
                <Link
                  key={job.id}
                  to={`/careers/${job.id}`}
                  data-testid={`job-card-${index}`}
                  className="group block p-6 bg-white border border-slate-200 hover:border-blue-600 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 font-['Outfit'] group-hover:text-blue-700 transition-colors">
                        {job.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span>{job.type}</span>
                        <span>{job.experience}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      View Details
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600">No open positions in this department at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Don't See the Right Role?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="careers-contact-button"
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
